/**
 * BUG-CATCHING PROGRESSION — Naturalist stats + a talent tree. This REPLACES the old gear-equip "bug kit"
 * (net/jar/boots/lens/lure slots). There is nothing to buy or equip: catching power comes from
 *   1) TWO stats that rise automatically EVERY Naturalist level, and
 *   2) TALENTS — every 5 levels the player picks 1 of 3 authored options, and may re-spec any pick freely.
 *
 * The two per-level stats:
 *   • Capture Rate — how fast Capture Chance builds while a bug is under the net (%/sec).
 *   • Escape Time  — seconds added to a bug's escape timer (longer pursuit → more time to build odds).
 *
 * Talents layer static buffs/effects on top (bigger net, retain-after-fail, rarity help, etc.). The resolved
 * `BugLoadout` matches the shape the catch minigame already reads (see ui/InsectLayer.tsx), so only the source
 * of the numbers changes, not the minigame.
 *
 * A TIER MUST POSE A QUESTION, NOT A SUM. The tree used to repeat itself badly: eight of the thirty options
 * were a flat capture-rate bump differing only in size and name, four were a flat net bump, four a flat
 * retain bump. A tier of three flat constants is not a choice — the answer is always "whichever number is
 * biggest at my level", which is arithmetic. Lv 20 was the worst: rate vs retain vs net, three constants.
 *
 * The fix was to give nine of the ten tiers at least one CONDITIONAL option — a talent keyed off game STATE
 * the minigame already tracks, the way `restBonus` and `rareGrace` already did. Four new mods do this, and
 * each keys off something `stepCatch`/`begin` already has in hand, so none needed new bookkeeping:
 *   • `openerRate`    (Pounce, Lv 10)      — pursuit elapsed  → rewards committing early
 *   • `calmPierce`    (Still Hands, Lv 20) — the bug's spook  → rewards approaching without panicking it
 *   • `skittishGrace` (Stalker, Lv 35)     — def.skittish     → nothing on placid bugs, most on jumpy ones
 *   • `closerRate`    (Last Lunge, Lv 45)  — time remaining   → the mirror of Pounce; rewards holding on
 * A flat option remains in every one of those tiers on purpose: it is the reliable baseline the conditional
 * gambles against, which is what makes the pick a real decision rather than a strictly-better swap.
 *
 * Lv 5 is deliberately left as three flat options (net / rate / escape). It is the FIRST pick a player ever
 * makes and it teaches the three basic axes; a conditional there would be a riddle before the vocabulary.
 *
 * **EVERY MOD MUST BE READ BY ui/InsectLayer.tsx.** A knob the minigame ignores is a lie printed on a talent
 * card. All eleven currently are — check that before adding a twelfth.
 */

// ── THE TWO PER-LEVEL STATS ────────────────────────────────────────────────────────────────────────────────
// Both grow linearly with level from a base, then map through a saturating curve to the concrete knob so no
// stat runs away. Tuned so a fresh Naturalist is capable and a maxed one is strong but not trivial.

// The two core stats are computed DIRECTLY IN THEIR REAL UNITS (Capture Rate in %/s, Escape Time in seconds) as
// a linear per-level growth PLUS the flat talent amounts, then GENTLY soft-capped. This is the key change from
// the old points-through-a-curve model: a talent that says "+1.0s" now adds a literal +1.0s to the shown Escape
// Time (before the cap), instead of adding pre-curve points whose real effect shrank to near zero at high level.

// THE RATE BUDGET IS +6 %/s, AND EVERY NUMBER BELOW IS A SHARE OF IT. This is the fact that governs the
// whole stat, and it is not obvious:
//   • Lv 1, no talents, on a common must stay ≈52%. A common is 6.0s of pursuit + 0.5s base escape, so
//     `rate × 6.5s = 52%` pins CAP_BASE at exactly 8.0 %/s. It is not a free knob.
//   • Lv 50, fully talented, on a legendary should be ≈80% (strong, never certain). A legendary is 4.0s at
//     0.55 resistance, cut by whatever pierce the build carries, plus the escape bonus — which pins the
//     FULL-STACK rate near 14 %/s.
// So 49 levels of growth AND all eight capture-rate talents together may move the rate 8.0 → 14.0. That is
// the entire budget. Talent amounts have to be shares of +6, which is why they read +0.3 … +0.7 rather than
// +4 … +9. An effect line promising "+9 %/s" was advertising more than the design has to give, at any
// setting of these constants.
//
// WHAT WAS WRONG BEFORE, and it is the exact failure the redesign at the top of this file exists to prevent
// — reintroduced through the cap instead of through a pre-curve points model. The talents advertised
// +4…+9 %/s (55 total) and the soft cap was the old exponential shape, which asymptotes at `cap × 1.4` and
// so has a FIXED ABSOLUTE HEADROOM. Measured marginal delivery, stacking in unlock order:
//
//   Lv    talent          advertised     delivered    % of advert
//    5    Steady Grip        +4            3.214          80%
//   10    Quick Study        +5            1.104          22%
//   20    Focus              +6            0.209         3.5%
//   25    Deep Study         +7            0.039         0.6%
//   30    Sure Grip          +8            0.005         0.1%
//   40    Capture Surge      +9            0.000         0.0%
//   45    Flow State         +8            0.000         0.0%
//   50    Apex Naturalist    +8            0.000         0.0%
//                          ────           ─────
//                            55            0.57         1.0%
//
// Five of the eight were mathematically incapable of doing anything. The whole tree moved the rate by
// half a percent.
//
// THE FIX IS TWO CHANGES THAT HAVE TO GO TOGETHER. Either alone fails.
//   1) `softCap` is now LOGARITHMIC rather than exponential-asymptotic — see its docblock. The old shape
//      could not deliver a ninth point no matter how it was tuned, because its total headroom was fixed.
//   2) The capture-rate talents were rescaled to shares of the real +6 budget. Raising the cap alone would
//      have kept the advertised numbers dishonest AND blown the ceiling back to a 100% legendary.
// Delivery is now 100.0% on every capture-rate talent at the level it unlocks (verified against this
// module). `restBonus` (Rest Watcher / Ambusher) was rescaled to match: it is applied AFTER the soft cap,
// so it was always delivered in full, and at its old +6/+10 it would now dwarf every other rate pick.
//
// Max chance = min(1, effRate × pursuitTime). What these values give, rounded the way the Insect Difficulty
// tool rounds them (Math.round of bugMatchup over RARITY_CATCH):
//
//                    no talents                    fully talented
//   Lv   com  unc  rare  epic  leg        com  unc  rare  epic  leg
//    1   52   42    32    23    16         52   42    32    23    16
//   10   58   47    36    26    19         63   51    39    29    20
//   20   65   54    41    30    21         74   62    48    36    27
//   30   73   60    46    34    25         91   76    59    45    34
//   40   80   66    51    38    27         99   84    95    77    62
//   50   87   72    55    42    30        100  100   100    99    79
//
// "Fully talented" is ONE named build, taken tier by tier as each unlocks: every capture-rate talent (Steady
// Grip, Quick Study, Focus, Deep Study, Sure Grip, Flow State, Apex Bug Catcher) plus Specialist (Lv 15),
// Entomologist (Lv 35) and Master's Eye (Lv 40). Master's Eye is why rare+ jumps past uncommon at Lv 40. It is
// a strong build, not the strongest: for every build and every level, see the Insect Difficulty tool
// (bugDifficulty.ts) and the all-builds sweep in src/sim/insectRoster.test.ts.
//
//   • Lv 1, no talents, common ......... 52.0%  — unchanged; the early game is untouched.
//   • Lv 50, fully talented, legendary . 79.3%  — strong, never certain. No build reaches 100% (RARE_GRACE_CAP).
//   • With no talents the curve stays live all the way to 50 (a common never becomes a certainty at all).
//
// KNOWN LIMIT — a fully-talented COMMON still reaches 100% around Lv 30. That is structural, not a tuning
// miss: a common is 6.0s at zero resistance where a legendary is 4.0s at 0.55 resistance, cut further by
// whatever pierce the build carries, so any rate putting the legendary near 80% hands the common far more
// than it needs.
// Commons capping out is correct — they are commons. Genuinely separating the tiers needs a lever these
// constants do not have: most cheaply, the per-tier `captureResistance` spread in RARITY_CATCH (insects.ts).
// Note pierce cannot help there — a common has ZERO resistance, so no pierce setting touches it at all.

// Capture Rate (%/s): base + per-level, + talent %/s. Soft-capped so it can't run away.
const CAP_BASE = 8, CAP_PER_LEVEL = 0.04, CAP_SOFTCAP = 14;      // L1 = 8%/s → L50 ≈ 9.96%/s (pre-talent)
// Escape Time BONUS (seconds added to a bug's timer): base + per-level, + talent seconds. Soft-capped.
const ESC_BASE = 0.5, ESC_PER_LEVEL = 0.05, ESC_SOFTCAP = 1.8;   // L1 = +0.5s → L50 ≈ +2.69s (pre-talent)

/**
 * Soft cap — LOGARITHMIC, not exponential-asymptotic, and the difference is the whole point.
 *
 * The old shape was `cap + cap·0.4·(1 − e^(−over/(cap·0.4)))`, which asymptotes at `cap × 1.4`. That gives a
 * FIXED ABSOLUTE HEADROOM: with CAP_SOFTCAP 10 nothing above the cap could ever add more than 4.0 %/s in
 * total, however much was stacked. Any sequence of talent picks therefore had to decay to zero delivery —
 * the later ones were mathematically incapable of doing anything, no matter what their effect line claimed.
 *
 * This shape has no ceiling; it just grows ever more slowly (`d/dv = cap/v`, so each point past the cap is
 * worth `cap/v` of itself). A runaway is still impossible in practice — reaching double the cap costs
 * `e−1 ≈ 1.7×` the cap in raw points, and the talent budget is nowhere near that — but a talent at the top
 * of the tree still moves the number. Do NOT restore an asymptote here without also shrinking the tree.
 */
function softCap(v: number, cap: number): number {
  if (v <= cap) return v;
  return cap + cap * Math.log(1 + (v - cap) / cap);
}

/** Capture Chance %/sec for a level with `addPct` of flat talent bonus (before the soft cap). */
export function captureRatePct(level: number, addPct = 0): number {
  return softCap(CAP_BASE + Math.max(0, level - 1) * CAP_PER_LEVEL + addPct, CAP_SOFTCAP);
}
/** Seconds ADDED to a bug's escape timer for a level with `addSec` of flat talent bonus (before the soft cap). */
export function escapeBonusSec(level: number, addSec = 0): number {
  return softCap(ESC_BASE + Math.max(0, level - 1) * ESC_PER_LEVEL + addSec, ESC_SOFTCAP);
}

// ── TALENTS ────────────────────────────────────────────────────────────────────────────────────────────────
// A talent is a static, always-on effect. It contributes to the loadout via `mods` — flat/percent adders the
// derivation folds in. Every 5 levels the player picks 1 of a tier's 3 options; they can re-spec any time.

/**
 * All the knobs a talent can modify — in REAL UNITS, applied additively across chosen talents.
 *
 * The mods split into two families, and the split is the design:
 *   • FLAT — a constant added regardless of situation (`captureRatePct`, `escapeSec`, `netRadiusPx`,
 *     `retainChance`, `resistPierce`). Useful, but three flat mods in one tier is not a choice: the answer
 *     is always "whichever number is biggest", which is arithmetic rather than a decision.
 *   • CONDITIONAL — keyed off game STATE the minigame already tracks, so the value depends on how you play
 *     and what you are hunting (`rareGrace`, `restBonus`, and the four below). These are what make a tier a
 *     real question: a conditional can beat a flat option in its own situation and lose outside it.
 *
 * EVERY MOD HERE MUST BE READ BY ui/InsectLayer.tsx. A knob the minigame ignores is a lie on a talent card.
 * The four conditional mods added below all key off state the catch loop already has in hand — pursuit time
 * remaining, the bug's `spook` meter, its `skittish` value — so none of them needed new bookkeeping.
 */
export interface TalentMods {
  captureRatePct?: number;  // + Capture Rate (%/sec, added flat before the soft cap)
  escapeSec?: number;       // + Escape Time (seconds, added flat before the soft cap)
  netRadiusPx?: number;     // + net circle radius (px)
  retainChance?: number;    // + retain-after-fail probability (0..1, capped)
  resistPierce?: number;    // 0..1 — reduces a bug's captureResistance (helps vs rare bugs), capped
  rareGrace?: number;       // + extra escape seconds that apply ONLY to rare+ bugs
  restBonus?: number;       // + capture rate %/sec specifically while a bug is RESTING (landed/settled)
  // ── conditional (state-keyed) ──
  openerRate?: number;      // + capture rate %/sec during the FIRST `OPENER_S` seconds of a pursuit
  closerRate?: number;      // + capture rate %/sec during the LAST `CLOSER_S` seconds before escape
  calmPierce?: number;      // 0..1 extra resistance pierce, scaled by how UNSPOOKED the bug is (1 − spook)
  skittishGrace?: number;   // + escape seconds scaled by the bug's own `skittish` (jumpy bugs give more)
}
/** Window (seconds) in which `openerRate` applies, measured from the start of a pursuit. */
export const OPENER_S = 1.5;
/** Window (seconds) in which `closerRate` applies, measured backwards from the escape. */
export const CLOSER_S = 1.5;

export interface BugTalent {
  id: string;
  name: string;
  effect: string;           // short, mechanical effect line (name + effect only — no flavour, no icon)
  mods: TalentMods;
}

/** Tiers unlock every 5 levels: Lv 5, 10, 15 … up to MAX_LEVEL. Each tier offers exactly 3 options; pick 1. */
export const TALENT_TIER_STEP = 5;
export const TALENT_TIERS: BugTalent[][] = [
  // ── Lv 5 ──
  [
    { id: "wideNet",    name: "Wide Net",        effect: "+8px net radius",                    mods: { netRadiusPx: 8 } },
    { id: "steady",     name: "Steady Grip",     effect: "+0.3%/s Capture Rate",               mods: { captureRatePct: 0.3 } },
    { id: "patient",    name: "Patient",         effect: "+1.0s Escape Time",                  mods: { escapeSec: 1.0 } },
  ],
  // ── Lv 10 ── flat rate vs a fast OPENER vs safety-net retain: rush it, or play for the long pursuit?
  [
    { id: "gentle",     name: "Gentle Hands",    effect: "+20% retain after a miss",           mods: { retainChance: 0.2 } },
    { id: "quickStudy", name: "Quick Study",     effect: "+0.4%/s Capture Rate",               mods: { captureRatePct: 0.4 } },
    { id: "pounce",     name: "Pounce",          effect: `+2.0%/s Capture Rate for the first ${OPENER_S}s`, mods: { openerRate: 2.0 } },
  ],
  // ── Lv 15 ──
  [
    { id: "specialist", name: "Specialist",      effect: "−8% insect resistance",              mods: { resistPierce: 0.08 } },
    { id: "longPursuit",name: "Long Pursuit",    effect: "+1.6s Escape Time",                  mods: { escapeSec: 1.6 } },
    { id: "restWatch",  name: "Rest Watcher",    effect: "+0.8%/s Capture Rate vs resting bugs", mods: { restBonus: 0.8 } },
  ],
  // ── Lv 20 ── the worst tier before this pass: rate vs retain vs net, three flat constants and no question.
  // Now: a flat rate, a bigger net, or a pierce that pays off only if you approach CALMLY (low spook).
  [
    { id: "focus",      name: "Focus",           effect: "+0.5%/s Capture Rate",               mods: { captureRatePct: 0.5 } },
    { id: "hugeNet",    name: "Grand Net",       effect: "+8px net radius",                    mods: { netRadiusPx: 8 } },
    { id: "stillHands", name: "Still Hands",     effect: "−20% insect resistance on a calm bug (fades as it spooks)", mods: { calmPierce: 0.20 } },
  ],
  // ── Lv 25 ──
  [
    { id: "rareGrace",  name: "Bug Catcher's Eye",effect: "+2.0s Escape Time vs rare+ bugs",    mods: { rareGrace: 2.0 } },
    { id: "deepStudy",  name: "Deep Study",      effect: "+0.5%/s Capture Rate",               mods: { captureRatePct: 0.5 } },
    { id: "piercer2",   name: "Insight",         effect: "−10% insect resistance",             mods: { resistPierce: 0.10 } },
  ],
  // ── Lv 30 ──
  [
    { id: "endurance",  name: "Endurance",       effect: "+2.2s Escape Time",                  mods: { escapeSec: 2.2 } },
    { id: "surgeGrip",  name: "Sure Grip",       effect: "+0.6%/s Capture Rate",               mods: { captureRatePct: 0.6 } },
    { id: "restMaster", name: "Ambusher",        effect: "+1.2%/s Capture Rate vs resting bugs",mods: { restBonus: 1.2 } },
  ],
  // ── Lv 35 ── net vs pierce vs a grace that scales with how JUMPY the quarry is (worthless on placid bugs,
  // strong on the skittish ones that are hardest to hold).
  [
    { id: "netMaster",  name: "Net Master",      effect: "+10px net radius",                   mods: { netRadiusPx: 10 } },
    { id: "stalker",    name: "Stalker",         effect: "+4.0s Escape Time × the bug's skittishness", mods: { skittishGrace: 4.0 } },
    { id: "piercer3",   name: "Entomologist",    effect: "−11% insect resistance",             mods: { resistPierce: 0.11 } },
  ],
  // ── Lv 40 ── flat rate vs rare-only grace vs the second-chance line. `marathon` (a flat +3.0s escape) used
  // to sit here and simply repeated Lv 30's Endurance a size larger, so it is now the retain option instead —
  // retain lost three of its four homes in this pass and needs one at the top of the tree to stay a line.
  [
    { id: "captureSurge",name:"Capture Surge",   effect: "+0.7%/s Capture Rate",               mods: { captureRatePct: 0.7 } },
    { id: "tenacious",  name: "Tenacious",       effect: "+30% retain after a miss",           mods: { retainChance: 0.3 } },
    { id: "rareGrace2", name: "Master's Eye",    effect: "+3.0s Escape Time vs rare+ bugs",    mods: { rareGrace: 3.0 } },
  ],
  // ── Lv 45 ── a solid all-rounder, a pierce, or a CLOSER that only pays if you hold on to the last moment —
  // the mirror of Pounce at Lv 10, and the two are mutually exclusive in play as well as on the sheet.
  [
    { id: "flowState",  name: "Flow State",      effect: "+0.6%/s Capture Rate & +6px net",    mods: { captureRatePct: 0.6, netRadiusPx: 6 } },
    { id: "lastLunge",  name: "Last Lunge",      effect: `+3.0%/s Capture Rate in the final ${CLOSER_S}s`, mods: { closerRate: 3.0 } },
    { id: "piercer4",   name: "Master Netter",   effect: "−12% insect resistance",             mods: { resistPierce: 0.12 } },
  ],
  // ── Lv 50 ──
  [
    { id: "apex",       name: "Apex Bug Catcher", effect: "+0.6%/s Capture Rate & +2.0s Escape", mods: { captureRatePct: 0.6, escapeSec: 2.0 } },
    { id: "trapper",    name: "Master Trapper",  effect: "+8px net radius & +30% retain",      mods: { netRadiusPx: 8, retainChance: 0.3 } },
    { id: "sage",       name: "Garden Sage",     effect: "−14% resistance & +2.5s vs rare+",   mods: { resistPierce: 0.14, rareGrace: 2.5 } },
  ],
];

/** How many talent TIERS exist (one per 5 levels up to the cap). */
export const TALENT_TIER_COUNT = TALENT_TIERS.length;
/** The Naturalist level at which talent tier `i` (0-based) unlocks. */
export function talentTierLevel(i: number): number { return (i + 1) * TALENT_TIER_STEP; }
/** How many talent tiers are UNLOCKED at a Naturalist level. */
export function unlockedTalentTiers(level: number): number {
  return Math.min(TALENT_TIER_COUNT, Math.floor(level / TALENT_TIER_STEP));
}
/** Look up a talent by id (searches all tiers). */
export function talentById(id: string): BugTalent | undefined {
  for (const tier of TALENT_TIERS) { const t = tier.find((x) => x.id === id); if (t) return t; }
  return undefined;
}

/** Is `id` one of the three options authored for tier `tierIndex`? */
export function isTalentInTier(tierIndex: number, id: string): boolean {
  return TALENT_TIERS[tierIndex]?.some((t) => t.id === id) ?? false;
}

/** The player's talent CHOICES: tier index (0-based) → chosen talent id. Absent = not yet picked. */
export type BugTalentChoices = Record<number, string>;

/**
 * A CHOICE IS ONLY VALID IN THE TIER THAT AUTHORED IT. `activeMods` resolves each id with `talentById`,
 * which searches every tier, so an id in the wrong slot still contributes its mods — and the choices map
 * is player-supplied save data. `{0:"sage",1:"sage",…}` used to stack one talent ten times, and a Lv 5
 * player could park the Lv 50 pick in tier 0 and have it counted (tier 0 is unlocked, so the level gate
 * never looked at it). The tier membership is the missing check, and it belongs on the way in: drop any
 * entry whose key is not a real tier index or whose id was not authored for that tier.
 */
export function coerceBugTalents(raw: unknown): BugTalentChoices {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: BugTalentChoices = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const i = Number(k);
    if (!Number.isInteger(i) || i < 0 || i >= TALENT_TIER_COUNT) continue;
    if (typeof v !== "string" || !isTalentInTier(i, v)) continue;
    out[i] = v;
  }
  return out;
}

/** Sum the mods of all chosen talents (ignoring tiers not yet unlocked at `level`). */
function activeMods(choices: BugTalentChoices, level: number): TalentMods {
  const out: TalentMods = {};
  const tiers = unlockedTalentTiers(level);
  for (let i = 0; i < tiers; i++) {
    const id = choices[i]; if (!id) continue;
    const t = talentById(id); if (!t) continue;
    for (const [k, v] of Object.entries(t.mods)) out[k as keyof TalentMods] = (out[k as keyof TalentMods] ?? 0) + (v as number);
  }
  return out;
}

// ── RESOLVED LOADOUT (what the catch minigame reads) ─────────────────────────────────────────────────────────
// Matches the old BugLoadout shape so InsectLayer needs no change, plus a couple of new talent-driven knobs the
// catch math can optionally use (resistPierce / rareGrace / restBonus).

export interface BugLoadout {
  netRadius: number;       // px radius of the net circle
  captureRate: number;     // Capture Chance %/sec while a bug is under the net
  escapeBonus: number;     // + seconds on a bug's escape timer
  retainChance: number;    // 0..1 retain-after-fail probability
  resistPierce: number;    // 0..1 — reduces a bug's captureResistance (helps vs rare bugs)
  rareGrace: number;       // + escape seconds applied ONLY to rare+ bugs
  restBonus: number;       // + capture rate %/sec while the bug is resting
  openerRate: number;      // + capture rate %/sec during the first OPENER_S of a pursuit
  closerRate: number;      // + capture rate %/sec during the last CLOSER_S before escape
  calmPierce: number;      // 0..1 extra pierce, scaled by (1 − the bug's spook)
  skittishGrace: number;   // + escape seconds scaled by the bug's own skittish value
}

const NET_BASE_PX = 30;              // base net radius before talents — snug so net-widening talents matter
const RETAIN_CAP = 0.6;              // hard cap on retain-after-fail
/**
 * PIERCE IS A REAL SPEC: the cap is REACHABLE, but only by spending every pierce tier.
 *
 * `resistPierce` is MULTIPLICATIVE — `resistanceAfter = base × (1 − pierce)` — so 1.0 means resistance is
 * fully negated, and every point is worth the same (there is no diminishing return and no natural ceiling
 * at the game's highest resistance of 0.55; that intuition only holds for a subtractive pierce).
 *
 * The five pierce talents (Specialist 0.08, Insight 0.10, Entomologist 0.11, Master Netter 0.12, Garden
 * Sage 0.14) sum to EXACTLY 0.55 = PIERCE_CAP. That is the design: each tier is worth taking on its own,
 * nothing is silently discarded, and only a player who spends all five of their ten picks on pierce reaches
 * the cap. They were 0.15/0.22/0.30/0.38/0.40 = 1.45 against a 0.5 cap, so about two thirds of a five-pick
 * commitment evaporated — and the game's own End-game reference build in bugDifficulty.ts took exactly
 * those five. **If a pierce amount changes, the five must still sum to PIERCE_CAP**, or the ladder either
 * overflows again or stops being able to reach it.
 *
 * WHY THE CAP IS 0.55 AND NOT 1.00. A cap of 1.00 is tempting — it is the point where resistance is fully
 * negated — but at 1.00 a full pierce spec catches EVERYTHING at 100%, legendaries included, because zero
 * resistance plus a Lv 50 rate is simply too much. 0.55 leaves a legendary at 0.55 × 0.45 = 0.247 effective
 * resistance even after total commitment, which lands it at ~86%: the strongest PIERCE build, and still not
 * a certainty. Do not raise this without re-checking the legendary. It is not the strongest build overall
 * (pierce mixed with time reaches ~94%), and the TIME line has a cap of its own: see RARE_GRACE_CAP.
 *
 * A full pierce spec is not overpowered because it costs the other five tiers: it forgoes every escape
 * talent and most rate talents to get there.
 */
export const PIERCE_CAP = 0.55;             // reachable, but only by spending all five pierce tiers (they sum to 0.55)

/**
 * "NEVER CERTAIN" IS A RULE, AND THIS IS WHAT ENFORCES IT FOR TIME. A legendary must stay below 100% at Lv 50
 * for EVERY build, not just the reference one. Pierce is capped above; extra pursuit time on rare+ bugs is
 * capped here.
 *
 * Three talents add seconds ONLY on rare+ bugs: Bug Catcher's Eye 2.0, Master's Eye 3.0, Garden Sage 2.5.
 * Uncapped they stack to 7.5 s. With Endurance and a few rate picks, that made a Lv 50 legendary a 100% catch.
 * Stalker (`skittishGrace`, up to 4.0 × 0.70 = 2.8 s on the jumpiest legendary) is the same kind of stack, and
 * capping rareGrace alone still let Stalker + one grace tier reach 100%. So the ceiling covers the SUM of both on
 * rare+ bugs (`bonusPursuitSec`, read by InsectLayer's begin() and by bugMatchup).
 *
 * Why 3.5 s, measured over every build × every legendary at Lv 50 (Stalker included):
 *   uncapped 100%   ·   4.0 → 98.2%   ·   3.5 → 94.1%   ·   3.0 → 91.4%
 * 3.5 is the largest value that stays clearly below certain. It is above every SINGLE talent's amount, so each
 * grace talent still delivers its full printed number when taken alone. Only a second grace source is clipped,
 * and the talent modal's measured marginal shows the clipped amount. The reference End-game build (one 2.0 s
 * tier) is untouched at 63%, and so is the 79% "fully talented" column above (Master's Eye alone).
 * Pinned by src/sim/insectRoster.test.ts. **Do not raise this, or add a rare+ time talent, without re-running
 * that sweep.**
 */
export const RARE_GRACE_CAP = 3.5;          // seconds; ceiling on rareGrace + Stalker's grace, rare+ bugs only

/** Seconds a loadout adds to ONE bug's escape timer on top of `escapeBonus`: Stalker's grace scaled by the bug's
 *  skittishness, plus rare grace on rare+ bugs. On rare+ bugs the sum is capped at RARE_GRACE_CAP. The catch
 *  minigame and the difficulty readout both call this, so the shown pursuit and the played pursuit agree. */
export function bonusPursuitSec(load: { rareGrace?: number; skittishGrace?: number }, rarePlus: boolean, skittish: number): number {
  const skit = (load.skittishGrace ?? 0) * skittish;
  return rarePlus ? Math.min(RARE_GRACE_CAP, (load.rareGrace ?? 0) + skit) : skit;
}

/** The resolved catching loadout for a Naturalist `level` with `choices` talents. Capture Rate & Escape Time are
 *  computed in real units (%/s, seconds) with the flat talent amounts added BEFORE the soft cap — so a "+1.0s"
 *  talent adds a real ~1.0s (see captureRatePct / escapeBonusSec). */
export function deriveBugLoadout(level: number, choices: BugTalentChoices): BugLoadout {
  const m = activeMods(choices, level);
  return {
    netRadius: NET_BASE_PX + (m.netRadiusPx ?? 0),
    captureRate: captureRatePct(level, m.captureRatePct ?? 0),
    escapeBonus: escapeBonusSec(level, m.escapeSec ?? 0),
    retainChance: Math.min(RETAIN_CAP, m.retainChance ?? 0),
    resistPierce: Math.min(PIERCE_CAP, m.resistPierce ?? 0),
    rareGrace: Math.min(RARE_GRACE_CAP, m.rareGrace ?? 0),   // Stalker shares this cap on rare+ bugs; see bonusPursuitSec
    restBonus: m.restBonus ?? 0,
    openerRate: m.openerRate ?? 0,
    closerRate: m.closerRate ?? 0,
    // `calmPierce` is added to `resistPierce` at USE time (scaled by 1 − spook), so it shares PIERCE_CAP
    // with the flat pierce rather than stacking past it — see InsectLayer's stepCatch.
    calmPierce: m.calmPierce ?? 0,
    skittishGrace: m.skittishGrace ?? 0,
  };
}

/** A neutral loadout (level 1, no talents) — for previews/tests. */
export const NEUTRAL_BUG_LOADOUT: BugLoadout = deriveBugLoadout(1, {});
